// ============================================================================
// Sezione "Report passati" del tab Report Social nella scheda cliente.
// Elenca i PDF già generati (archivio del servizio social-report-bymarke) con
// i KPI del periodo e il delta rispetto al report precedente, per permettere
// i confronti mese su mese. Il PDF resta scaricabile dal link pubblico /dl.
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import { FileText, ExternalLink, Loader2, TrendingUp, TrendingDown, Minus, Trash2 } from 'lucide-react';

interface ReportKpis {
  followers?: number | null;
  reach?: number | null;
  views?: number | null;
  interactions?: number | null;
  erPct?: number | null;
}

interface ReportSnapshot {
  id: string;
  generatedAt: string;
  mode: 'monthly' | 'custom' | null;
  since: string | null;
  until: string | null;
  kpis: ReportKpis | null;
  sizeBytes: number | null;
  url: string | null;
}

interface Props {
  clienteId: string;
  // Incrementato dal genitore quando viene generato un nuovo report → ricarica
  refreshKey?: number;
}

const MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

// "2026-06-01".."2026-06-30" → "Giugno 2026"; altrimenti "01/06/26 → 15/06/26"
function labelPeriodo(since: string | null, until: string | null): string {
  if (!since || !until) return '—';
  const s = new Date(since);
  const u = new Date(until);
  const fineMese = new Date(u.getFullYear(), u.getMonth() + 1, 0).getDate();
  if (
    s.getFullYear() === u.getFullYear() && s.getMonth() === u.getMonth() &&
    s.getDate() === 1 && u.getDate() === fineMese
  ) {
    return `${MESI[s.getMonth()]} ${s.getFullYear()}`;
  }
  const fmt = (d: Date) => d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' });
  return `${fmt(s)} → ${fmt(u)}`;
}

const num = (v: number | null | undefined): string =>
  v === null || v === undefined ? '—' : v.toLocaleString('it-IT');

// Delta rispetto al report precedente (in ordine cronologico) con freccia colorata
const Delta: React.FC<{ curr?: number | null; prev?: number | null; pct?: boolean }> = ({ curr, prev, pct }) => {
  if (curr === null || curr === undefined || prev === null || prev === undefined) return null;
  const diff = Math.round((curr - prev) * 100) / 100;
  if (diff === 0) {
    return <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-400"><Minus size={10} /></span>;
  }
  const up = diff > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${up ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`}>
      {up ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
      {up ? '+' : ''}{pct ? diff.toFixed(2) : diff.toLocaleString('it-IT')}
    </span>
  );
};

const ReportStorico: React.FC<Props> = ({ clienteId, refreshKey = 0 }) => {
  const [reports, setReports] = useState<ReportSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Id del report in corso di eliminazione (per lo spinner sul pulsante)
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/reports/history/${clienteId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('bymarke_token')}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        setError(json.message || 'Storico non disponibile.');
        setReports([]);
      } else {
        setReports(json.reports || []);
      }
    } catch {
      setError('Errore di rete nel caricamento dello storico.');
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleDelete = async (r: ReportSnapshot) => {
    const periodo = labelPeriodo(r.since, r.until);
    if (!window.confirm(`Eliminare il report "${periodo}" dallo storico? Anche il PDF verrà rimosso e l'azione non può essere annullata.`)) return;
    setDeletingId(r.id);
    try {
      const res = await fetch(`/api/reports/history/${clienteId}/${r.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('bymarke_token')}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        alert(json.message || 'Eliminazione fallita, riprova.');
      } else {
        setReports((prev) => prev.filter((x) => x.id !== r.id));
      }
    } catch {
      alert('Errore di rete: riprova tra poco.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-1">
        <FileText size={18} className="text-slate-400" />
        <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100" style={{ letterSpacing: '-0.02em' }}>
          Report passati
        </h2>
      </div>
      <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mb-4">
        Ogni report generato viene salvato qui con i KPI del periodo: le frecce indicano la variazione rispetto al report precedente.
      </p>

      {loading ? (
        <div className="py-8 text-center text-slate-500 dark:text-slate-400">
          <Loader2 size={20} className="animate-spin mx-auto mb-2" />
          <span className="text-sm">Caricamento storico…</span>
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/30 px-3 py-2.5 text-sm text-red-700 dark:text-red-300">{error}</div>
      ) : reports.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
          Nessun report generato finora: il primo che generi comparirà qui.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500 border-b border-slate-200 dark:border-gray-700">
                <th className="py-2 pr-3 font-semibold">Periodo</th>
                <th className="py-2 pr-3 font-semibold">Generato il</th>
                <th className="py-2 pr-3 font-semibold text-right">Follower</th>
                <th className="py-2 pr-3 font-semibold text-right">Reach</th>
                <th className="py-2 pr-3 font-semibold text-right">Interazioni</th>
                <th className="py-2 pr-3 font-semibold text-right">ER %</th>
                <th className="py-2 pr-3 font-semibold text-right">PDF</th>
                <th className="py-2 font-semibold text-right"></th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r, i) => {
                // reports è ordinato dal più recente: il "precedente" è la riga dopo
                const prev = reports[i + 1]?.kpis || null;
                return (
                  <tr key={r.id} className="border-b border-slate-100 dark:border-gray-700/60 last:border-0">
                    <td className="py-2.5 pr-3 font-medium text-slate-900 dark:text-slate-100 whitespace-nowrap">
                      {labelPeriodo(r.since, r.until)}
                    </td>
                    <td className="py-2.5 pr-3 text-slate-500 dark:text-slate-400 whitespace-nowrap">
                      {r.generatedAt ? new Date(r.generatedAt).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—'}
                    </td>
                    <td className="py-2.5 pr-3 text-right whitespace-nowrap text-slate-700 dark:text-slate-300">
                      {num(r.kpis?.followers)}{' '}
                      <Delta curr={r.kpis?.followers} prev={prev?.followers} />
                    </td>
                    <td className="py-2.5 pr-3 text-right whitespace-nowrap text-slate-700 dark:text-slate-300">
                      {num(r.kpis?.reach)}{' '}
                      <Delta curr={r.kpis?.reach} prev={prev?.reach} />
                    </td>
                    <td className="py-2.5 pr-3 text-right whitespace-nowrap text-slate-700 dark:text-slate-300">
                      {num(r.kpis?.interactions)}{' '}
                      <Delta curr={r.kpis?.interactions} prev={prev?.interactions} />
                    </td>
                    <td className="py-2.5 pr-3 text-right whitespace-nowrap text-slate-700 dark:text-slate-300">
                      {r.kpis?.erPct === null || r.kpis?.erPct === undefined ? '—' : `${r.kpis.erPct}%`}{' '}
                      <Delta curr={r.kpis?.erPct} prev={prev?.erPct} pct />
                    </td>
                    <td className="py-2.5 pr-3 text-right whitespace-nowrap">
                      {r.url ? (
                        <a
                          href={r.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 dark:border-gray-600 px-2 py-1 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          <ExternalLink size={12} /> Apri
                        </a>
                      ) : (
                        <span className="text-xs text-slate-400" title="PDF non più disponibile sul server">n.d.</span>
                      )}
                    </td>
                    <td className="py-2.5 text-right whitespace-nowrap">
                      <button
                        onClick={() => handleDelete(r)}
                        disabled={deletingId === r.id}
                        className="inline-flex items-center justify-center p-1.5 rounded-lg text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50"
                        title="Elimina report dallo storico"
                      >
                        {deletingId === r.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ReportStorico;
